import { createSlice } from "@reduxjs/toolkit";
import { findDocumentsTypes } from "./actions/findDocumentTypes";
import { findTotalPeople } from "./actions/findTotalPeople";
import { sendNotifications } from "./actions/sendNotifications";
import { User } from "../../@types/user";
import { Role } from "../../@types/role";
import { findPeople } from "./actions/findPeople";
import { Location } from "../../@types/location";
import { sendPeopleNotifications } from "./actions/sendPeopleNotifications";

interface People {
  id: number;
  name: string;
  role: string;
  location: Location;
  code: string;
}

interface DocumentTypeWithCounts {
  id: number;
  name: string;
  documentCount: number;
  peopleCount: number;
}

interface MassNotificationState {
  documentTypesList: DocumentTypeWithCounts[];
  peopleList: People[];
  findPeopleStatus: "idle" | "pending" | "succeeded" | "failed";
  findDocumentTypesStatus: "idle" | "pending" | "succeeded" | "failed";
  totalPeople: number;
  findTotalPeopleStatus: "idle" | "pending" | "succeeded" | "failed";
  sendNotificationStatus: "idle" | "pending" | "succeeded" | "failed";
  sendPeopleNotificationStatus: "idle" | "pending" | "succeeded" | "failed";
  selectedDocumentTypes: DocumentTypeWithCounts[];
  selectedPeople: People[];
  email: boolean;
  whatsapp: boolean;
  createdAtFilter?: {
    from: number;
    to: number;
  };
  createFilters: {
    location: Location;
    range: string;
  };
  showFilterByDateModal: boolean;
  searchText: string;
}

const initialState = {
  documentTypesList: [],
  peopleList: [],
  findPeopleStatus: "idle",
  findDocumentTypesStatus: "idle",
  totalPeople: 0,
  findTotalPeopleStatus: "idle",
  sendNotificationStatus: "idle",
  sendPeopleNotificationStatus: "idle",
  selectedDocumentTypes: [],
  selectedPeople: [],
  email: false,
  whatsapp: false,
  createdAtFilter: {
    from: 0,
    to: 0,
  },
  createFilters: {
    location: {} as Location,
    range: "",
  },
  showFilterByDateModal: false,
  searchText: "",
} as MassNotificationState;

const massNotificationSlice = createSlice({
  name: "massNotification",
  initialState,
  reducers: {
    setInitialData(state) {
      return { ...initialState };
    },
    setUpdateList(state) {
      state.documentTypesList = [];
      state.totalPeople = 0;
      state.selectedDocumentTypes = [];
    },
    setPreselectPeople(state, { payload }) {
      state.selectedPeople = [...state.selectedPeople, payload];
    },
    setPreselectAllPeople(state, { payload }) {
      state.selectedPeople = [...state.selectedPeople, ...payload];
    },
    setDeselectAllPeople(state) {
      state.selectedPeople = [];
    },
    setDeselectPeople(state, { payload }) {
      state.selectedPeople = state.selectedPeople.filter(({ id }) => id !== payload.id);
    },
    setPreselectDocumentType(state, { payload }) {
      state.selectedDocumentTypes = [...state.selectedDocumentTypes, payload];
    },
    setPreselectAll(state, { payload }) {
      state.selectedDocumentTypes = [...state.selectedDocumentTypes, ...payload];
    },
    setDeselectDocumentType(state, { payload }) {
      state.selectedDocumentTypes = state.selectedDocumentTypes.filter(
        ({ id }) => id !== payload.id
      );
    },
    setDeselectAll(state) {
      state.selectedDocumentTypes = [];
    },
    setEmail(state, { payload }) {
      state.email = payload;
    },
    setWhatsapp(state, { payload }) {
      state.whatsapp = payload;
    },
    setCreatedAtFilter(state, { payload }) {
      state.createdAtFilter = payload;
    },
    setFilterByDateModal(state, { payload }) {
      state.showFilterByDateModal = payload;
    },
    setSearchText(state, { payload }) {
      state.searchText = payload;
    },
    setLocation(state, { payload }) {
      state.createFilters.location = payload;
    },
    setRange(state, { payload }) {
      state.createFilters.range = payload;
    },
    clearCreateFilters(state) {
      state.createFilters = {
        location: {} as Location,
        range: "",
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendNotifications.pending, (state) => {
        state.sendNotificationStatus = "pending";
      })
      .addCase(sendNotifications.fulfilled, (state, { payload }) => {
        state.sendNotificationStatus = "succeeded";
      })
      .addCase(sendNotifications.rejected, (state) => {
        state.sendNotificationStatus = "failed";
      })
      .addCase(sendPeopleNotifications.pending, (state) => {
        state.sendPeopleNotificationStatus = "pending";
      })
      .addCase(sendPeopleNotifications.fulfilled, (state, { payload }) => {
        state.sendPeopleNotificationStatus = "succeeded";
      })
      .addCase(sendPeopleNotifications.rejected, (state) => {
        state.sendPeopleNotificationStatus = "failed";
      })
      .addCase(findTotalPeople.pending, (state) => {
        state.findTotalPeopleStatus = "pending";
        state.totalPeople = 0;
      })
      .addCase(findTotalPeople.fulfilled, (state, { payload }) => {
        state.findTotalPeopleStatus = "succeeded";
        state.totalPeople = payload;
      })
      .addCase(findTotalPeople.rejected, (state) => {
        state.findTotalPeopleStatus = "failed";
        state.totalPeople = 0;
      })
      .addCase(findDocumentsTypes.pending, (state) => {
        state.findDocumentTypesStatus = "pending";
        state.documentTypesList = [];
      })
      .addCase(findDocumentsTypes.fulfilled, (state, { payload }) => {
        state.findDocumentTypesStatus = "succeeded";
        state.documentTypesList = payload;
      })
      .addCase(findDocumentsTypes.rejected, (state) => {
        state.findDocumentTypesStatus = "failed";
        state.documentTypesList = [];
      })
      .addCase(findPeople.pending, (state) => {
        state.findPeopleStatus = "pending";
        state.peopleList = [];
      })
      .addCase(findPeople.fulfilled, (state, { payload }) => {
        state.findPeopleStatus = "succeeded";
        state.peopleList = payload;
      })
      .addCase(findPeople.rejected, (state) => {
        state.findPeopleStatus = "failed";
        state.peopleList = [];
      });
  },
});

export const {
  setInitialData,
  setPreselectAll,
  setPreselectDocumentType,
  setDeselectAll,
  setDeselectDocumentType,
  setEmail,
  setWhatsapp,
  setCreatedAtFilter,
  setFilterByDateModal,
  setUpdateList,
  setDeselectAllPeople,
  setDeselectPeople,
  setPreselectAllPeople,
  setPreselectPeople,
  setLocation,
  setRange,
  setSearchText,
  clearCreateFilters,
} = massNotificationSlice.actions;

export default massNotificationSlice.reducer;
